import React, { useState, useEffect } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface Slide {
  id: string;
  image: string;
  title: string;
  description?: string;
  link?: string;
}

interface ImageSlideshowProps {
  slides: Slide[];
  autoPlay?: boolean;
  interval?: number;
  showNavigation?: boolean;
  showIndicators?: boolean;
  height?: string | number;
}

const ImageSlideshow: React.FC<ImageSlideshowProps> = ({
  slides,
  autoPlay = true,
  interval = 5000,
  showNavigation = true,
  showIndicators = true,
  height = 400,
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  useEffect(() => {
    if (!autoPlay) return;

    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, interval);

    return () => clearInterval(timer);
  }, [autoPlay, interval, slides.length]);

  const handlePrevious = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const handleNext = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const handleIndicatorClick = (index: number) => {
    setCurrentSlide(index);
  };

  const handleSlideClick = (slide: Slide) => {
    if (slide.link) {
      navigate(slide.link);
    }
  };

  if (!slides.length) return null;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: isMobile ? 300 : height,
        overflow: 'hidden',
        borderRadius: 2,
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      }}
    >
      {/* Slides Container */}
      <Box
        sx={{
          display: 'flex',
          width: `${slides.length * 100}%`,
          height: '100%',
          transform: `translateX(-${(currentSlide * 100) / slides.length}%)`,
          transition: 'transform 0.5s ease-in-out',
        }}
      >
        {slides.map((slide, index) => (
          <Box
            key={slide.id}
            sx={{
              width: `${100 / slides.length}%`,
              height: '100%',
              position: 'relative',
              cursor: slide.link ? 'pointer' : 'default',
            }}
            onClick={() => handleSlideClick(slide)}
          >
            <Box
              component="img"
              src={slide.image}
              alt={slide.title}
              loading="lazy"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transition: slide.link ? 'transform 0.3s ease' : 'none',
                '&:hover': slide.link ? {
                  transform: 'scale(1.05)',
                } : {},
              }}
            />
            {/* Overlay with text */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                background: `
                  linear-gradient(transparent 0%, rgba(0,0,0,0.3) 30%, rgba(0,0,0,0.8) 100%)
                `,
                color: 'white',
                padding: { xs: 2, md: 3 },
                paddingTop: { xs: 4, md: 6 },
              }}
            > 
              {/* Additional dark backdrop for better readability */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0,0,0,0.4)',
                  borderRadius: '0 0 8px 8px',
                }}
              />
              
              {/* Text content with enhanced styling */}
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Typography
                  variant={isMobile ? "h6" : "h5"}
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    color: '#ffffff',
                    textShadow: '3px 3px 6px rgba(0,0,0,0.8)',
                    fontSize: { xs: '1.25rem', md: '1.5rem' },
                    lineHeight: 1.2,
                  }}
                >
                  {slide.title}
                </Typography>
                {slide.description && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#e0e0e0',
                      textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                      fontSize: { xs: '0.875rem', md: '1rem' },
                      lineHeight: 1.4,
                      fontWeight: 400,
                    }}
                  >
                    {slide.description}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Navigation Arrows */}
      {showNavigation && slides.length > 1 && (
        <>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handlePrevious();
            }}
            sx={{
              position: 'absolute',
              left: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(255,255,255,0.9)',
              color: '#333',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,1)',
              },
              zIndex: 2,
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleNext();
            }}
            sx={{
              position: 'absolute',
              right: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(255,255,255,0.9)',
              color: '#333',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,1)',
              },
              zIndex: 2,
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </>
      )}

      {/* Indicators */}
      {showIndicators && slides.length > 1 && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 1,
            zIndex: 2,
          }}
        >
          {slides.map((_, index) => (
            <Box
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                handleIndicatorClick(index);
              }}
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: index === currentSlide ? 'white' : 'rgba(255,255,255,0.5)',
                cursor: 'pointer',
                transition: 'background-color 0.3s ease',
                '&:hover': {
                  backgroundColor: index === currentSlide ? 'white' : 'rgba(255,255,255,0.8)',
                },
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ImageSlideshow; 